import {
  useBasketEvents,
  useShopEvents,
  useWishlistEvents,
  useProductEvents,
  useCheckoutEvents,
  useSearchEvents,
  usePurchaseEvents,
  usePromotionEvents,
  useUserActionEvents,
  useCustomEvents,
  useYouTubeEvents,
  useNavigationEvents,
  useBlogEvents,
  useOdoscopeEvents,
  useExperimentsEvents,
} from './events'
import {
  mapProductToTrackingPayload,
  enrichTrackingData,
} from '~/utils/tracking'
import type { TrackingEvent, TrackingPayload } from '~/types/tracking'
import { useState } from '#app/composables/state'

/**
 * Higher order composable which serves as a single entry point to unify different ecommerce tracking events
 */
const useTrackingEvents = (useServerQueue?: boolean) => {
  const { $tracking } = useNuxtApp()
  const route = useRoute()

  const serverQueue = useState<any[]>('serverQueue', () => [])

  // Returns whether the event was actually pushed/enqueued. Callers that dedup
  // on a successful send (e.g. carousel impression tracking) rely on this so a
  // not-ready `$tracking` doesn't permanently drop an impression.
  const track = (event: TrackingEvent, payload: TrackingPayload): boolean => {
    const trackingData = enrichTrackingData(event, payload, route)

    if (import.meta.server) {
      if (useServerQueue) {
        serverQueue.value = [...serverQueue.value, trackingData]
        return true
      }
      return false
    }

    // Guard: if `$tracking` isn't ready yet, no-op instead of throwing so callers
    // can tell the push didn't land.
    if (!$tracking?.push) {
      return false
    }
    $tracking.push(trackingData)
    return true
  }

  const fireTrackingServerQueue = () => {
    if (useServerQueue) {
      serverQueue.value.map((trackingData) => $tracking.push(trackingData))
      serverQueue.value = []
    }
  }

  /**
   * NOTE: Important considerations:
   * sub-composable(s) such as useBasketEvents, etc should not be utilized directly
   * instead make use of the higher order composable (useTrackingEvents) to keep things clean
   * new events should be added to relevant sub-composable to ensure DRY code and separation of concerns
   */

  return {
    ...useShopEvents(track),
    ...useBasketEvents(track),
    ...useWishlistEvents(track),
    ...useProductEvents(track),
    ...useCheckoutEvents(track),
    ...useSearchEvents(track),
    ...usePurchaseEvents(track, { $tracking }),
    ...usePromotionEvents(track),
    ...useUserActionEvents(track, { $tracking }),
    ...useCustomEvents(track),
    ...useYouTubeEvents(track),
    ...useNavigationEvents(track),
    ...useBlogEvents(track),
    ...useOdoscopeEvents(track),
    ...useExperimentsEvents(track),
    mapProductToTrackingPayload,
    fireTrackingServerQueue,
  }
}

export default useTrackingEvents
